import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileDown, Save } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STATUSES = ['new', 'contacted', 'won', 'lost'];
const STATUS_COLORS = { new: '#D97904', contacted: '#2563eb', won: '#16a34a', lost: '#6b7280' };
const fmt = (n) => `$${(n || 0).toLocaleString()}`;

const QuoteRow = ({ quote, onSaved }) => {
  const [status, setStatus] = useState(quote.status || 'new');
  const [notes, setNotes] = useState(quote.notes || '');
  const [saving, setSaving] = useState(false);
  const dirty = status !== (quote.status || 'new') || notes !== (quote.notes || '');

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${BACKEND_URL}/api/admin/quotes/${quote.id}`, { status, notes });
      onSaved(data);
      toast.success('Quote updated');
    } catch {
      toast.error('Could not save quote');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 border border-gray-200 rounded-lg hover:border-orange-300 transition-colors" data-testid={`quote-row-${quote.id}`}>
      <div className="flex flex-wrap justify-between items-start gap-3 mb-3">
        <div>
          <h4 className="font-semibold text-gray-900">{quote.name} <span className="text-gray-400 font-normal">#{quote.id.slice(0, 8).toUpperCase()}</span></h4>
          <a href={`mailto:${quote.email}`} className="text-sm text-blue-600 hover:underline">{quote.email}</a>
          {quote.phone && <span className="text-sm text-gray-600"> · {quote.phone}</span>}
          {quote.company && <span className="text-sm text-gray-600"> · {quote.company}</span>}
        </div>
        <div className="flex items-center gap-2">
          {quote.follow_up_sent_at && <Badge variant="outline" className="text-xs">Reminder sent</Badge>}
          <Badge style={{ backgroundColor: STATUS_COLORS[quote.status || 'new'] }} className="text-white capitalize" data-testid={`quote-status-badge-${quote.id}`}>
            {quote.status || 'new'}
          </Badge>
        </div>
      </div>
      <ul className="text-sm text-gray-700 mb-3 space-y-1">
        {quote.lines.map((ln) => (
          <li key={ln.slug} className="flex justify-between">
            <span>{ln.title} <span className="text-gray-400">· {ln.tier}</span></span>
            <span className="font-semibold">{fmt(ln.price)}{ln.monthly ? '/mo' : ''}</span>
          </li>
        ))}
      </ul>
      <div className="grid grid-cols-3 gap-3 text-sm mb-3 bg-gray-50 p-3 rounded">
        <div><p className="text-xs text-gray-500">Monthly</p><p className="font-bold">{fmt(quote.monthly_total)}</p></div>
        <div><p className="text-xs text-gray-500">One-time</p><p className="font-bold">{fmt(quote.one_time_total)}</p></div>
        <div><p className="text-xs text-gray-500">Savings</p><p className="font-bold" style={{ color: '#D97904' }}>{fmt(quote.savings)}</p></div>
      </div>
      <div className="grid sm:grid-cols-[160px_1fr_auto] gap-3 items-start">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid={`quote-status-select-${quote.id}`} className="capitalize"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Textarea
          data-testid={`quote-notes-${quote.id}`}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Follow-up notes…"
          className="min-h-[40px]"
          rows={1}
        />
        <div className="flex gap-2">
          <Button size="sm" onClick={save} disabled={!dirty || saving} data-testid={`quote-save-${quote.id}`} className="text-white" style={{ backgroundColor: '#D97904' }}>
            <Save className="w-4 h-4 mr-1" /> Save
          </Button>
          <Button size="sm" variant="outline" asChild>
            <a href={`${BACKEND_URL}/api/quote/${quote.id}/pdf`} target="_blank" rel="noopener noreferrer" data-testid={`quote-pdf-${quote.id}`}>
              <FileDown className="w-4 h-4" />
            </a>
          </Button>
        </div>
      </div>
      <div className="mt-3 text-xs text-gray-500">Requested: {new Date(quote.created_at).toLocaleString()}</div>
    </div>
  );
};

const QuotesTab = () => {
  const [data, setData] = useState({ quotes: [], counts: {}, total: 0 });
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/quotes?limit=100`);
      setData(res.data);
    } catch {
      toast.error('Could not load quotes');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSaved = (updated) =>
    setData((d) => {
      const quotes = d.quotes.map((q) => (q.id === updated.id ? updated : q));
      const counts = Object.fromEntries(STATUSES.map((s) => [s, quotes.filter((q) => (q.status || 'new') === s).length]));
      return { ...d, quotes, counts };
    });
  const visible = filter === 'all' ? data.quotes : data.quotes.filter((q) => (q.status || 'new') === filter);

  return (
    <Card data-testid="quotes-tab">
      <CardHeader>
        <CardTitle>Quote Requests</CardTitle>
        <CardDescription>{data.total} total · pipeline by status</CardDescription>
        <div className="flex flex-wrap gap-2 pt-2">
          {['all', ...STATUSES].map((s) => (
            <button
              key={s}
              data-testid={`quote-filter-${s}`}
              onClick={() => setFilter(s)}
              className={`text-xs px-3 py-1 rounded-full border capitalize ${filter === s ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-300 text-gray-700'}`}
            >
              {s} {s !== 'all' && <span className="opacity-70">({data.counts[s] || 0})</span>}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {visible.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="quotes-empty">No quotes yet.</p>
        ) : (
          <div className="space-y-4">
            {visible.map((q) => <QuoteRow key={q.id} quote={q} onSaved={onSaved} />)}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default QuotesTab;
